import { useEffect, useState } from "react";
import { Workflow } from "lucide-react";
import { Handle, Position, type Node, type NodeProps } from "@xyflow/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { StartAutomationNodeData } from "./types";

interface AutomationLite {
  _id: string;
  name?: string;
}

const AUTOMATION_NONE = "__none__";

export function StartAutomationNode({ data }: NodeProps<Node<StartAutomationNodeData>>) {
  const { title = "Start Automation", automationId, isSaving, onAutomationChange } = data;
  const [automations, setAutomations] = useState<AutomationLite[]>([]);
  const [isLoadingAutomations, setIsLoadingAutomations] = useState(true);

  useEffect(() => {
    fetch("/api/automations")
      .then((res) => res.json())
      .then((payload) => {
        const loaded = (payload.automations as AutomationLite[] | undefined) ?? [];
        setAutomations(loaded.filter((entry) => entry._id));
      })
      .catch(() => {})
      .finally(() => setIsLoadingAutomations(false));
  }, []);

  return (
    <div className="bg-white rounded-xl border-2 border-emerald-300 shadow-md w-[280px] relative">
      <Handle
        type="target"
        position={Position.Left}
        className="!bg-emerald-400 !w-3 !h-3 !border-2 !border-white"
        style={{ top: "22px", left: 0 }}
      />
      <div className="px-4 py-3 bg-emerald-50 rounded-xl border-b border-emerald-200">
        <div className="flex min-w-0 items-center gap-2">
          <Workflow className="h-4 w-4 shrink-0 text-emerald-500" />
          <span className="font-semibold text-sm text-emerald-700">{title}</span>
        </div>
        <div className="mt-3 nodrag nopan pointer-events-auto">
          <Select
            value={automationId?.trim() ? automationId : AUTOMATION_NONE}
            disabled={isSaving}
            onValueChange={(value) =>
              onAutomationChange(value === AUTOMATION_NONE ? "" : value)
            }
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={isLoadingAutomations ? "Loading..." : "Select flow"}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTOMATION_NONE}>None</SelectItem>
              {automations.map((automation) => (
                <SelectItem key={automation._id} value={automation._id}>
                  {automation.name?.trim() || automation._id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
